import { ObjectSchema, Schema, isObjectSchema } from '../types';
import validateSchema, { hasErrors } from './validateSchema';

/**
 * Evaluate a JSON schema if-then-else
 *
 * The evaluator expects the schema given in each of the
 * clauses to be of type object.
 */
function evaluateIf(schema: ObjectSchema, values: { [key: string]: any }) {
  if (!isObjectSchema(schema)) {
    throw new Error('Tried to evaluate an if that is not a ObjectSchema');
  }

  const { properties = {} } = schema;
  return !Object.keys(properties)
    .map(property => validateSchema(properties[property], values[property]))
    .some(hasErrors);
}

/**
 * Resolves a JSON schema with against a set of
 * values by evaluating the if-then-else conditions on the
 * given values to produce a new JSON schema without conditionals.
 *
 * The result from this function can be be rendered as a form
 * using the form-renderer. This function should and must be invoked
 * after each change of form values to ensure the conditions are always
 * met.
 *
 * The function does not support JSON schema constructs such as
 * oneOf or anyOf, only allOf. Currently our forms can be described
 * using a combination of allOf and if-then-elses.
 */
function resolve(schema: Schema, parent: Schema | null, values: any): Schema {
  const $uiOptions = schema.$uiOptions || {};

  if (isObjectSchema(schema)) {
    const properties = { ...(schema.properties || {}) };
    const required = schema.required || [];

    if (schema.if) {
      if (!schema.then || !isObjectSchema(schema.then)) {
        throw new Error(
          `If must have a corresponding then. ${JSON.stringify(
            schema.then
          )} given.`
        );
      }

      const branch = evaluateIf(schema.if, values) ? schema.then : schema.else;
      const resolvedBranch =
        branch != null ? resolve(branch, schema, values) : null;

      if (resolvedBranch) {
        if (!isObjectSchema(resolvedBranch)) {
          throw new Error(
            `If-then-else blocks must be ObjectSchemas. Type ${JSON.stringify(
              resolvedBranch
            )} was given`
          );
        }

        if (resolvedBranch.required) {
          required.push(...resolvedBranch.required);
        }

        Object.assign(properties, resolvedBranch.properties);
      }
    }

    if (schema.allOf) {
      // eslint-disable-next-line no-restricted-syntax
      for (const subschema of schema.allOf) {
        const resolved = resolve(subschema, schema, values);
        if (isObjectSchema(resolved)) {
          if (resolved.required) {
            required.push(...resolved.required);
          }

          Object.assign(properties, resolved.properties);
        }
      }
    }

    return {
      type: 'object',
      properties,
      $uiOptions,
      required: [...new Set(required)]
    };
  }

  return schema;
}

/**
 * Utility function for resolving an array of schemas.
 *
 * claim-forms implements form wizard steps using an array
 * of schemas.
 */
function resolveArrayOfObjectSchemas(
  schemas: Array<ObjectSchema>,
  values = {}
): Array<ObjectSchema> {
  return schemas
    .map(schema => resolve(schema, null, values))
    .filter(isObjectSchema);
}

/**
 * Check if an Object Schema as at least one property.
 */
function isNonEmptyObjectSchema(schema: Schema) {
  return !!(
    isObjectSchema(schema) &&
    schema.properties &&
    Object.keys(schema.properties).length > 0
  );
}

/**
 * Evaluates an array of schemas against a set of values
 * producing a new array of schemas with conditionals
 * being resolved.
 */
export default function evaluate(schemas: Array<ObjectSchema>, values: any) {
  return resolveArrayOfObjectSchemas(schemas, values).filter(
    isNonEmptyObjectSchema
  );
}

export function evaluateSubschema(schema: Schema, values: any) {
  return resolve(schema, null, values);
}
